/**
 * Product condition policy (owner decision, 19 Oct 2026).
 *
 * Every PHYSICAL product offered for sale is either «جديد» or «مستعمل».
 * There is no third option for users and the condition is never assumed:
 * the seller must choose it. Non-physical listings (real estate, services,
 * lessons, jobs, help wanted, live animals, animal services, car rental)
 * carry no condition at all.
 *
 * Consumables, food, perfumes and cosmetics ARE physical products: they
 * follow the same new/used rule (no subcategory exemptions).
 *
 * Legacy store own products created before this rule may still hold NULL
 * in the database until their owners choose; NULL is not a value users can
 * pick and is never offered in the UI.
 *
 * Shared by the server (validation, search) and the client (forms,
 * filters, badges) so both apply exactly the same rule.
 */

import { CATEGORIES } from "./constants";

export const PRODUCT_CONDITIONS = ["جديد", "مستعمل"] as const;
export type ProductCondition = (typeof PRODUCT_CONDITIONS)[number];

export function isProductCondition(value: unknown): value is ProductCondition {
  return value === "جديد" || value === "مستعمل";
}

/** Categories whose listings are not physical products. */
export const NON_PHYSICAL_CATEGORIES: readonly string[] = [
  "emlak",              // real estate
  "hizmetler",          // services
  "ozel-ders",          // private lessons
  "is-ilanlari",        // jobs
  "yardimci-arayanlar", // help wanted
];

/**
 * Animals: live animals and animal services carry no condition; the
 * physical supplies sold in this category do.
 */
export const ANIMAL_PHYSICAL_SUBCATEGORIES: readonly string[] = [
  "حوض أسماك", "معدات أحواض",
  "طعام كلاب", "طعام قطط", "طعام طيور", "طعام حيوانات",
  "إكسسوارات حيوانات", "أقفاص", "بيوت حيوانات", "ألعاب حيوانات", "أطواق ومقاود",
];

/** Service subcategories inside otherwise physical categories. */
export const SERVICE_SUBCATEGORIES: Readonly<Record<string, readonly string[]>> = {
  vasita: ["سيارات للإيجار"],
};

/**
 * Some older rows store the category's Arabic NAME instead of its key
 * (e.g. «العقارات» for `emlak`). Every value that means this category.
 */
export function categoryValues(key: string): string[] {
  const c = (CATEGORIES as Record<string, { name?: string; nameAr?: string }>)[key];
  return Array.from(new Set([key, c?.name, c?.nameAr].filter((v): v is string => !!v)));
}

const CATEGORY_KEY_BY_VALUE = new Map<string, string>(
  Object.keys(CATEGORIES).flatMap((key) => categoryValues(key).map((v) => [v, key] as [string, string])),
);

/** Category key for a stored value (key or Arabic name). */
export function resolveCategoryKey(category: string): string {
  return CATEGORY_KEY_BY_VALUE.get(category.trim()) ?? category.trim();
}

/**
 * Does a product in this category / subcategory need a «جديد / مستعمل»
 * choice? Unknown categories default to yes (never silently skip it).
 */
export function requiresProductCondition(
  category: string | null | undefined,
  subcategory?: string | null,
): boolean {
  if (!category) return false;
  const key = resolveCategoryKey(category);
  if (NON_PHYSICAL_CATEGORIES.includes(key)) return false;
  const sub = (subcategory ?? "").trim();
  if (key === "hayvanlar-alemi") return ANIMAL_PHYSICAL_SUBCATEGORIES.includes(sub);
  if (sub && SERVICE_SUBCATEGORIES[key]?.includes(sub)) return false;
  return true;
}

/**
 * Store own products are cart (physical) products: an old row saved with no
 * category (the category was optional before June 2026) still needs a
 * condition. Use this on own-product paths; forms keep requiresProductCondition
 * (no field before a category is chosen).
 */
export function ownProductRequiresCondition(
  category: string | null | undefined,
  subcategory?: string | null,
): boolean {
  return !category?.trim() || requiresProductCondition(category, subcategory);
}

/**
 * A stored condition as one of the two choices, keeping a seller's explicit
 * legacy detail («مستعمل - ممتاز» → «مستعمل», «جديد (0 كم)» → «جديد»).
 * Anything else → null (never assumed).
 */
export function normalizeStoredCondition(value: unknown): ProductCondition | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (isProductCondition(v)) return v;
  if (v.startsWith("مستعمل")) return "مستعمل";
  if (v.startsWith("جديد")) return "جديد";
  return null;
}

/**
 * Server refusal shown to EVERY client (website, apps, API) — no source or
 * app version bypasses the rule. Installed 1.0.5 apps have no condition
 * field; 1.0.6, which has it, is on both stores since 2026-10-26 (same
 * minimum iOS / Android versions), so the text asks to update the app, with
 * the website as the fallback (owner decision 2026-10-27; it pointed to the
 * website only before 1.0.6 existed).
 */
export const CONDITION_REQUIRED_MESSAGE =
  "حدّد حالة المنتج: «جديد» أو «مستعمل». إذا لم يظهر لك خيار الحالة في تطبيق الهاتف، حدّث التطبيق إلى آخر إصدار من المتجر، أو انشر المنتج من موقع سوق العراق: iraq-sooq.com";

/** Refusal for editing or reactivating a product saved before the rule. */
export const CONDITION_MISSING_MESSAGE =
  "هذا المنتج أُضيف قبل اعتماد حالة المنتج. حدّد حالته («جديد» أو «مستعمل») قبل حفظ أي تعديل أو إعادة تفعيله. إذا لم يظهر لك هذا الخيار في تطبيق الهاتف، حدّث التطبيق إلى آخر إصدار من المتجر، أو استخدم موقع سوق العراق: iraq-sooq.com";

/**
 * Search filter value: only the two real conditions narrow results;
 * anything else (empty, "all", legacy text) means no condition filter.
 */
export function normalizeConditionFilter(value: unknown): ProductCondition | undefined {
  return typeof value === "string" && isProductCondition(value.trim()) ? (value.trim() as ProductCondition) : undefined;
}
